<template>
  <div class="view-system">
    <div class="view-system--container">
      <div class="view-system-background"></div>
      <div class="view-system-scrollcontainer">
        <!-- System-Indicator -->
        <div class="view-system-status-indicator">
          <system-status
            :deviceData="latestPower3ParameterPublisherApp"
            :heartbeat="latestPower3DeviceHeartbeatApp"
            v-if="latestPower3DeviceHeartbeatApp"
          />
        </div>
        <div class="view-system-content">
          <!-- Headline -->
          <h1>{{ $t('Web.System.Headline') }}</h1>

          <!-- Status Battery -->
          <div class="view-system-status">
            <div class="view-system-status-container">
              <!-- Link List -->
              <div class="view-system-status-container__item view-system-status-links">
                <cactus-button-icon @btnClicked="showModalExpertview = true" :icon="'expertview'">
                  {{ $t('Web.System.Buttons.ExpertMode') }}
                </cactus-button-icon>
                <cactus-button-icon
                  @btnClicked="showModal = true"
                  :icon="'servicemode'"
                  v-if="!isTechnician"
                >
                  {{ $t('Web.ServiceMode.Title') }}
                </cactus-button-icon>
                <cactus-button-icon
                  :routeDestination="'devicelist'"
                  :icon="'devicelist'"
                  v-if="userTotalDevices > 1"
                >
                  {{ $t('Web.system_button_system_list') }}
                </cactus-button-icon>
              </div>

              <h3>{{ $t('Web.System.Battery') }}</h3>
              <div class="view-system-status-container__item">
                <battery-status
                  :managerApp="latestPower3ParameterManagerApp"
                  :batteryData="latestLgBatteryApp"
                  v-if="device"
                />
              </div>

              <h3>{{ $t('Web.system_electricity_price') }}</h3>
              <div class="view-system-status-container__item">
                <price-input />
              </div>
            </div>
          </div>

          <!-- Device information -->
          <div class="view-system-status-footer">
            <meta-data />
          </div>
        </div>
      </div>

      <modal v-if="showModal" @close="showModal = false">
        <div slot="header">
          <h1>{{ $t('Web.ServiceMode.Title') }}</h1>
        </div>

        <div slot="body">
          <service-mode :deviceSerial="this.device.serial"></service-mode>
        </div>

        <div slot="footer"></div>
      </modal>
      <modal v-if="showModalExpertview" @close="showModalExpertview = false">
        <div slot="header">
          <h1>{{ $t('Web.System.Buttons.ExpertMode') }}</h1>
        </div>

        <div slot="body">
          <expert-view />
        </div>

        <div slot="footer"></div>
      </modal>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { getStorageItem } from '../../helpers/persistence';

import BatteryStatus from '../_common/batteryStatus';
import CactusButtonIcon from '../_common/cactusButtonIcon';
import MetaData from '../_common/metaData';
import SystemStatus from '../_common/systemStatus';
import Modal from '../_common/modal';
import ExpertView from '../views/ExpertView';
import PriceInput from '../_common/priceInput';

const INTERVAL = 1000 * 10;

export default {
  name: 'System',
  components: {
    BatteryStatus,
    CactusButtonIcon,
    ExpertView,
    MetaData,
    SystemStatus,
    Modal,
    PriceInput,
    ServiceMode: () => import('../_common/serviceMode.vue')
  },
  data() {
    return {
      interval: undefined,
      showModal: false,
      showModalExpertview: false,
      userTotalDevices: null
    };
  },
  created() {
    if (this.device) {
      this.$nextTick(() => {
        this.getCurrentData();
        this.pollDeviceData(INTERVAL);
      });
    }

    this.userTotalDevices = getStorageItem('totalDevices');
  },
  beforeDestroy() {
    clearInterval(this.interval);
  },
  computed: {
    ...mapGetters([
      'isTechnician',
      'device',
      'latestPower3ParameterPublisherApp',
      'latestPower3ParameterManagerApp',
      'latestLgBatteryApp',
      'deviceList',
      'latestPower3DeviceHeartbeatApp'
    ])
  },
  methods: {
    ...mapActions(['getLatestAppData']),

    pollDeviceData(time) {
      time = time ? time : INTERVAL;
      clearInterval(this.interval);

      this.interval = setInterval(() => {
        if ((this.device || {}).serial) {
          this.getCurrentData();
          this.pollDeviceData(time);
        }
      }, time);
    },

    getCurrentData() {
      this.getLatestAppData(this.getQueryObject(this.device.serial));
    },

    getQueryObject(sn) {
      return {
        deviceSerialNumber: sn,
        requestGranularity: '1h',
        requestStartTime: this.$moment(new Date())
          .startOf('day')
          .format('YYYY-MM-DD[T]HH:mm:ssZ'),
        requestEndTime: this.$moment(new Date())
          .endOf('day')
          .format('YYYY-MM-DD[T]HH:mm:ssZ'),
        version: '1.0'
      };
    }
  }
};
</script>

<style lang="scss" scoped>
.view-system-background {
  background: $charcoal-grey url('../../assets/img/img-battery-big-no-bg.svg') no-repeat center 56px;
  position: fixed;
  height: 160px;
  top: 0;
  width: 100%;
  z-index: 1;
}

.view-system-content {
  position: relative;
  z-index: 0;
  margin: 112px 0 60px 0;
  padding-top: 60px;
  background: white;

  h1 {
    @include type-headline-variant1($greeny-blue);
    margin-bottom: 40px;
    text-align: center;
  }
}

.view-system-status-container {
  width: $maxWidth;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;

  h3 {
    margin: 0 0 10px 8px;
    width: 100%;
  }

  &__item {
    display: flex;
    margin-bottom: 40px;
    justify-content: space-between;

    .cactusButtonIcon {
      flex: 1 0 auto;
    }
  }
}

.view-system-status-indicator {
  position: fixed;
  top: 140px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  z-index: 3;
}

.view-system-status {
  h3 {
    @include textSmall;
    margin: 0 0 10px 8px;
    text-align: left;
    margin-bottom: 10px;
    color: $greeny-blue;
    font-weight: normal;
  }
}

.view-system-status-links {
  text-align: center;
  margin-top: 25px;
  width: 90%;

  @include breakpoint(sm) {
    margin-top: 0;
  }
}

.view-system-status-footer {
  margin-top: 20px;
}
</style>
